/**
 * Card Add Sheet — Payme card tokenization flow.
 * Step 1: Enter card number + expiry → SMS sent
 * Step 2: Enter SMS code → Card verified & saved
 */

import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CreditCard, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import { useCreateCard, useVerifyCard } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'

interface CardAddSheetProps {
  isOpen: boolean
  onClose: () => void
}

type Step = 'card_input' | 'sms_verify'

export const CardAddSheet: React.FC<CardAddSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const createCardMutation = useCreateCard()
  const verifyCardMutation = useVerifyCard()

  const [step, setStep] = useState<Step>('card_input')
  const [cardNumber, setCardNumber] = useState('')
  const [cardExpire, setCardExpire] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [cardToken, setCardToken] = useState('')
  const [cardId, setCardId] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [error, setError] = useState('')

  const numberInputRef = useRef<HTMLInputElement>(null)
  const smsInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && step === 'card_input') {
      setTimeout(() => numberInputRef.current?.focus(), 300)
    }
    if (isOpen && step === 'sms_verify') {
      setTimeout(() => smsInputRef.current?.focus(), 100)
    }
  }, [isOpen, step])

  // Reset on close
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isOpen) {
      setStep('card_input')
      setCardNumber('')
      setCardExpire('')
      setSmsCode('')
      setCardToken('')
      setCardId('')
      setMaskedPhone('')
      setError('')
    }
  }, [isOpen])
  /* eslint-enable react-hooks/set-state-in-effect */

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 16)
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  const formatExpire = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 4)
    if (digits.length >= 3) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`
    }
    return digits
  }

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCardNumber(formatCardNumber(e.target.value))
    setError('')
  }

  const handleExpireChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCardExpire(formatExpire(e.target.value))
    setError('')
  }

  const handleSubmitCard = (): void => {
    const cleanNumber = cardNumber.replace(/\s/g, '')
    const cleanExpire = cardExpire.replace('/', '')

    if (cleanNumber.length !== 16) {
      setError('Karta raqami 16 ta raqam bo\'lishi kerak')
      return
    }
    if (cleanExpire.length !== 4) {
      setError('Amal qilish muddati MM/YY formatida bo\'lishi kerak')
      return
    }

    hapticImpact('light')
    setError('')

    createCardMutation.mutate(
      { cardNumber: cleanNumber, cardExpire: cleanExpire },
      {
        onSuccess: (data) => {
          setCardToken(data.token)
          setCardId(data.card_id)
          setMaskedPhone(data.phone)
          setStep('sms_verify')
          hapticNotification('success')
        },
        onError: (err) => {
          setError(err.message)
          hapticNotification('error')
        },
      },
    )
  }

  const handleVerifySms = (): void => {
    if (smsCode.length !== 6) {
      setError('Kod 6 ta raqam bo\'lishi kerak')
      return
    }

    hapticImpact('light')
    setError('')

    verifyCardMutation.mutate(
      { cardId, token: cardToken, code: smsCode },
      {
        onSuccess: () => {
          hapticNotification('success')
          onClose()
        },
        onError: (err) => {
          setError(err.message)
          hapticNotification('error')
        },
      },
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-[55]"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-[55] inset-x-0 bottom-0 bg-surface rounded-t-[28px] border-t border-white/[0.06]"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/[0.12]" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4">
              <h3 className="text-lg font-display font-bold text-text">
                {t('cards.addCardTitle')}
              </h3>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/[0.04]"
              >
                <X size={18} className="text-text-hint" />
              </button>
            </div>

            <div className="px-5 pb-8">
              {step === 'card_input' && (
                <div className="space-y-4">
                  {/* Card Number */}
                  <div>
                    <label className="text-xs font-medium text-text-hint mb-1.5 block">
                      {t('cards.cardNumber')}
                    </label>
                    <div className="relative">
                      <CreditCard size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-hint/50" />
                      <input
                        ref={numberInputRef}
                        type="tel"
                        inputMode="numeric"
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="0000 0000 0000 0000"
                        maxLength={19}
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06]
                          text-text text-base font-mono tracking-wider placeholder:text-text-hint/30
                          focus:outline-none focus:border-primary/30"
                      />
                    </div>
                  </div>

                  {/* Expiry */}
                  <div>
                    <label className="text-xs font-medium text-text-hint mb-1.5 block">
                      {t('cards.cardExpire')}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={cardExpire}
                        onChange={handleExpireChange}
                        placeholder="MM/YY"
                        maxLength={5}
                        className="w-32 px-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06]
                          text-text text-base font-mono tracking-wider placeholder:text-text-hint/30
                          focus:outline-none focus:border-primary/30"
                      />
                      <span className="text-xs text-text-hint/50 flex items-center gap-1">
                        {t('cards.poweredBy')}
                        <span className="font-bold text-[#00CCCC]">pay</span>
                        <span className="font-bold text-text-hint">me</span>
                      </span>
                    </div>
                  </div>

                  {error && (
                    <p className="text-xs text-danger">{error}</p>
                  )}

                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleSubmitCard}
                    isLoading={createCardMutation.isPending}
                    disabled={cardNumber.replace(/\s/g, '').length < 16 || cardExpire.length < 5}
                  >
                    {t('cards.addCard')}
                  </Button>
                </div>
              )}

              {step === 'sms_verify' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
                    <ShieldCheck size={20} className="text-primary shrink-0" />
                    <p className="text-sm text-text-hint">
                      {t('cards.smsCodeSent')} ({maskedPhone})
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-text-hint mb-1.5 block">
                      {t('cards.smsCode')}
                    </label>
                    <input
                      ref={smsInputRef}
                      type="tel"
                      inputMode="numeric"
                      value={smsCode}
                      onChange={(e) => {
                        setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                        setError('')
                      }}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06]
                        text-text text-xl font-mono tracking-[0.5em] text-center placeholder:text-text-hint/30
                        focus:outline-none focus:border-primary/30"
                    />
                  </div>

                  {error && (
                    <p className="text-xs text-danger">{error}</p>
                  )}

                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleVerifySms}
                    isLoading={verifyCardMutation.isPending}
                    disabled={smsCode.length < 6}
                  >
                    {t('cards.verify')}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
